import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import LanguageContext from "../../helper/LanguageContext";
import appConfig from "../../config/appConfig";
import { resolveCockpitPersona, PERSONA_IO, PERSONA_PLUS } from "../../utils/cockpitPersona";
import { resolveStripeFlags } from "../../utils/stripeFeature";
import { canShowEmailCalendarIntegration } from "../../utils/kjuupIoEntitlement";
import prefetchKjuupIoApp from "../../utils/prefetchKjuupIoApp";
import { fetchAndStoreSubscriptionStatus } from "../../services/PaymentService";
import { callAPI } from "../../helper/apiUtils";
import { $crud } from "../../helper/CrudFactory";
import { DECREMENT_UNSEEN_ALL_SCOPE_DAILY_BRIEFING_COUNT, DECREMENT_UNSEEN_DAILY_BRIEFING_COUNT, SET_OPEN_SIDE_BAR } from "../../store";
import KjuupIoSetupModal from "../../components/KjuupIoSetupModal";
import useCockpitData from "./useCockpitData";
import CockpitHeader from "./components/CockpitHeader";
import CockpitKpis from "./components/CockpitKpis";
import CockpitChannels from "./components/CockpitChannels";
import CockpitDailyReportCard, { roomsWithReportActivity } from "./components/CockpitDailyReportCard";
import CockpitEmailBanner from "./components/CockpitEmailBanner";
import CockpitIncomingEmails from "./components/CockpitIncomingEmails";
import CockpitCompanyPanel from "./components/CockpitCompanyPanel";
import CockpitRoomAdminCard from "./components/CockpitRoomAdminCard";
import CockpitKjuupPlusAbo from "./components/CockpitKjuupPlusAbo";
import CompanyUnpublishedArticles from "../Company/CompanyUnpublishedArticles";
import { BOOKMARKED_PRIORITIES_ID, prioritiesFromEmail, roomsCoveredByReport } from "./components/CockpitBriefingText";
import { CockpitBookmarkedPriorities, CockpitMyTasksCard, MY_TASKS_CARD_ID } from "./components/CockpitTodoLists";
import CockpitTaskView, { CockpitTaskContext } from "./components/CockpitTaskView";
import { formatGreetingDate } from "./cockpitFormat";
import { Card } from "./components/CockpitPrimitives";
import { CockpitKjuupUpsell, CockpitSetupCard } from "./components/CockpitUpsellCards";
import { COCKPIT_TASK_GROUPS, cockpitDayKey, cockpitGroupOf, cockpitTaskDay, dismissPriority, fetchBookmarkedTodos, fetchReportTasks, forgetReport, syncPriorityBookmarks } from "./priorityBookmarks";
import { getReportJob, onReportJobFinished, resumeReportJob, startReportJob, subscribeReportJob, tickReportJob } from "./reportJob";
import {
  openAddCompany,
  openCompanyDocuments,
  openCompanySettingsFor,
  openExternal,
  openMessagesPage,
  openMyCompanies,
  openRoomsList,
  openSettings,
} from "./cockpitNavigation";
import "./Cockpit.css";

function reportMessages(data) {
  if (!data) return [];
  const rows = Array.isArray(data.messages)
    ? data.messages
    : Array.isArray(data.data)
      ? data.data
      : [];
  return rows.filter((m) => {
    if (!m) return false;
    const labels = Array.isArray(m.labels) ? m.labels : [];
    return !labels.some((l) => String(l).toUpperCase() === "TRASH");
  });
}

const TAB_OVERVIEW = "overview";
const TAB_COMPANY = "company";

// Company admins only. ROLE_COMPANY_ADMIN is the department admin and does not
// reach the company-wide rooms section.
function viewerManagesCompanyRooms(company) {
  if (!company || company.id == null) return false;
  if (company.isSuperAdmin || company.myRole === "ROLE_COMPANY_SUPER_ADMIN") return true;
  return String(company.myPosition || "").trim().toLowerCase() === "company admin";
}
// Stable fallback so memo deps don't change on every render.
const EMPTY_OBJECT = Object.freeze({});
// A task-list fetch this recent is reused rather than repeated.
const TASK_FETCH_REUSE_MS = 5000;

function isCockpitPathname(pathname) {
  return pathname === "/" || pathname === "/cockpit";
}

export default function Cockpit() {
  const { strings, language } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const user = useSelector((s) => s.user);
  const profile = useSelector((s) => s.profile);
  const subscription = useSelector((s) => s.subscription || EMPTY_OBJECT);
  const reduxProjects = useSelector((s) => s.projects);
  const internalProjectsAll = useSelector((s) => s.internalProjectsAll);
  const isDeskTopResolution = useSelector((s) => s.isDeskTopResolution);

  const active = isCockpitPathname(location.pathname);

  useEffect(() => {
    if (!active) return;
    dispatch({ type: SET_OPEN_SIDE_BAR, payload: "cockpit" });
  }, [active, dispatch]);
  const personaInfo = useMemo(() => resolveCockpitPersona(profile, subscription, user), [profile, subscription, user]);
  const { persona, isKjuupPlusMember, chatsEnabled, emailMode, kjuupIoSetupDone } = personaInfo;

  // Same entitlement math as LatestMessages so the email sections agree.
  const emailAvailable = useMemo(() => {
    const flags = resolveStripeFlags(profile, subscription);
    const hasActiveKjuupIoPlan = !flags.stripeUiEnabled || !!subscription.active;
    const enabled = canShowEmailCalendarIntegration({
      isKjuupPlusMember: flags.isKjuupPlusMember,
      hasActiveKjuupIoPlan,
      showCompanyTrialEndedNote: !!subscription.showCompanyTrialEndedNote,
      stripeFeatureEnabled: flags.stripeFeatureEnabled,
      companyStripeFeatureEnabled: flags.companyStripeFeatureEnabled,
    });
    return enabled && hasActiveKjuupIoPlan;
  }, [profile, subscription]);

  const isIo = persona === PERSONA_IO;
  const isPlus = persona === PERSONA_PLUS;
  const showEmailFeatures = isIo || emailAvailable;
  const showChats = !(isIo && chatsEnabled === false);
  const showEmailClient = showEmailFeatures && emailMode !== "reports";
  // Daily reports are listed on the cockpit only (no Berichte tab in Nachrichten).
  const showReports = isIo && showEmailFeatures;

  const { overview, reports, loading, error, lastLoadedAt, refresh, setOverview, setReports } = useCockpitData({
    active,
    withChats: false,
    withEmails: false,
    withReports: showReports,
  });

  const reportKey = (r) => String((r && (r.id || r._id || r.messageId)) || "");
  const reportIsAllScope = (r) =>
    !!r && (String(r.providerMessageId || "").includes("-all-") || (!r.buildingId && !(Array.isArray(r.linkedProjects) && r.linkedProjects.length)));
  const decrementReportBadge = (r) => {
    if (reportIsAllScope(r)) {
      dispatch({ type: DECREMENT_UNSEEN_ALL_SCOPE_DAILY_BRIEFING_COUNT });
      return;
    }
    const linked = Array.isArray(r.linkedProjects) ? r.linkedProjects[0] : null;
    dispatch({
      type: DECREMENT_UNSEEN_DAILY_BRIEFING_COUNT,
      buildingId: (linked && (linked.id || linked.buildingId)) || r.buildingId,
      isInternal: !!(linked && (linked.isInternal || linked.buildingProjectType)),
    });
  };
  const patchLatestReport = (id, patch) =>
    setOverview((prev) =>
      prev && prev.latestDailyBriefing && reportKey(prev.latestDailyBriefing) === id
        ? { ...prev, latestDailyBriefing: patch ? { ...prev.latestDailyBriefing, ...patch } : null }
        : prev
    );

  // Expanding a report on the cockpit marks it read (same as opening it in the inbox did).
  const markReportRead = async (report) => {
    const id = reportKey(report);
    if (!id || report.isRead === true || report.isRead === 1 || report.isRead === "1") return;
    setReports((prev) => prev.map((r) => (reportKey(r) === id ? { ...r, isRead: true } : r)));
    setRoomReports((prev) => prev.map((r) => (reportKey(r) === id ? { ...r, isRead: true } : r)));
    patchLatestReport(id, { isRead: true });
    decrementReportBadge(report);
    try {
      await callAPI(`/email/messages/${encodeURIComponent(id)}`, {}, "PATCH", { isRead: true });
    } catch (_) {
      /* the next overview refresh reconciles the flag */
    }
  };

  // Delete from the document toolbar. Mirrors the inbox: room-report link first,
  // then the message itself. Returns false when cancelled / failed.
  const deleteReport = async (report) => {
    const id = reportKey(report);
    if (!id) return false;
    try {
      await $crud.confirm({
        title: strings.email_delete || "Löschen",
        textContent: strings.cockpit_report_delete_confirm || "Diesen Tagesbericht löschen?",
        options: { ok: strings.ok || "OK", cancel: strings.cancel || "Abbrechen" },
      });
    } catch (_) {
      return false;
    }
    try {
      let res = await callAPI(`/email/project-messages/${encodeURIComponent(id)}`, {}, "DELETE");
      if (res?.data?.status === false) {
        res = await callAPI(`/email/messages/${encodeURIComponent(id)}`, {}, "DELETE");
      }
      if (res?.data?.status === false) {
        throw new Error(res.data.message || "delete failed");
      }
      const wasUnread = !(report.isRead === true || report.isRead === 1 || report.isRead === "1");
      if (wasUnread) decrementReportBadge(report);
      setReports((prev) => prev.filter((r) => reportKey(r) !== id));
      setRoomReports((prev) => prev.filter((r) => reportKey(r) !== id));
      patchLatestReport(id, null);
      // Only the sync record goes; the bookmarked priorities stay.
      forgetReport(id).catch(() => {});
      $crud.notify({ type: "success", message: strings.cockpit_report_deleted || "Tagesbericht gelöscht" });
      return true;
    } catch (e) {
      $crud.notify({ type: "error", message: (e && e.message) || strings.something_went_wrong || "Something went wrong" });
      return false;
    }
  };

  // Entitlement can change while the cockpit is open (trial start etc.).
  const [statusFailed, setStatusFailed] = useState(false);
  useEffect(() => {
    if (!active) return;
    try {
      Promise.resolve(fetchAndStoreSubscriptionStatus(dispatch))
        .then((status) => setStatusFailed(!status))
        .catch(() => setStatusFailed(true));
    } catch (_) {
      /* entitlement refresh is best effort */
    }
  }, [active, dispatch]);
  // Until the first status arrives a Kjuup.io user still resolves to the plain
  // Kjuup persona, so the upsell waits for it instead of flashing.
  const subscriptionPending = !subscription.lastFetchedAt && !statusFailed;

  // The Abo page runs a second app; fetch it once the cockpit itself is idle.
  useEffect(() => {
    if (!active || persona !== PERSONA_IO) return undefined;
    const timer = setTimeout(prefetchKjuupIoApp, 2500);
    return () => clearTimeout(timer);
  }, [active, persona]);

  const [tab, setTab] = useState(TAB_OVERVIEW);
  // A task opened from the cockpit ({ todoId, roomId, roomName }) shows in the Meine Aufgaben
  // card, in place of its list; the card's back arrow returns to the list. The rest stays.
  const [openTask, setOpenTask] = useState(null);
  const tasksSideRef = useRef(null);
  const openTaskHere = useCallback((item) => {
    if (!item || item.todoId == null || item.todoId === "") return;
    setOpenTask({ todoId: String(item.todoId), roomId: item.roomId || item.buildingList || null, roomName: item.roomName || "" });
    // Scrolled down the list: bring the card's top (where the task opens) into view.
    requestAnimationFrame(() => {
      const side = tasksSideRef.current;
      if (side && side.getBoundingClientRect().top < 0) side.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);
  const closeTask = useCallback(() => {
    setOpenTask(null);
    refresh();
  }, [refresh]);
  // Any navigation (sidebar "Mein Cockpit", leaving and coming back) shows the overview.
  // The day Meine Aufgaben shows ("YYYY-MM-DD", from the day strip); null is today.
  const [taskDay, setTaskDay] = useState(null);
  useEffect(() => {
    setOpenTask(null);
    setTaskDay(null);
  }, [location.key]);
  const [roomFilter, setRoomFilter] = useState(null);
  // Rows under "Fristen & Aufgaben" and "Termine" in the task list beside the
  // report (null while the report's list is not shown).
  const [listedCounts, setListedCounts] = useState(null);
  // The tiles switched on above the list ("tasks", "appointment"): it shows
  // only those groups. One always stays on.
  const [taskGroups, setTaskGroups] = useState(() => COCKPIT_TASK_GROUPS.map((group) => group.key));
  // Passed up after every render of the list: the same numbers keep the state.
  const onGroupCounts = useCallback(
    (next) => setListedCounts((prev) => (
      prev && next && prev.appointment === next.appointment && prev.tasks === next.tasks ? prev : next
    )),
    []
  );
  const [roomReports, setRoomReports] = useState([]);
  const [roomReportsLoading, setRoomReportsLoading] = useState(false);
  // The report being created lives outside the page (reportJob): it keeps
  // running while the user is elsewhere in the app.
  const reportJob = useSyncExternalStore(subscribeReportJob, getReportJob);
  const creatingReport = !!reportJob;
  const reportProgress = reportJob ? reportJob.progress : null;
  const [setupOpen, setSetupOpen] = useState(false);
  const emailSectionRef = useRef(null);
  const roomFetchRef = useRef(0);

  // "/?section=email" (sidebar Kjuup-Email in reports mode) -> scroll to the banner.
  useEffect(() => {
    if (!active) return;
    const params = new URLSearchParams(location.search || "");
    const section = params.get("section");
    if (section === "email" && emailSectionRef.current) {
      setTab(TAB_OVERVIEW);
      setTimeout(() => emailSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
    // "packages" (Settings › plan card › Upgrade) -> the top of all package offers.
    if (section === "kjuup-io" || section === "kjuup-plus" || section === "packages") {
      setTab(TAB_OVERVIEW);
      const offerId =
        section === "packages" ? "cockpit-offers" : section === "kjuup-plus" ? "cockpit-offer-kjuup-plus" : "cockpit-offer-kjuup-io";
      setTimeout(() => {
        document.getElementById(offerId)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
    if (params.get("tab") === "company") setTab(TAB_COMPANY);
    else if (params.get("tab") === "overview") setTab(TAB_OVERVIEW);
  }, [active, location.search]);

  const overviewCompanies = overview?.companies?.length ? overview.companies : overview?.company ? [overview.company] : [];
  const [memberCompanies, setMemberCompanies] = useState([]);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    callAPI("/company/my", {}, "GET")
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.status ? res.data.companies || [] : [];
        setMemberCompanies(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setMemberCompanies([]);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);
  const companies = useMemo(() => {
    const byId = new Map();
    overviewCompanies.forEach((c) => {
      if (c && c.id != null) byId.set(String(c.id), c);
    });
    memberCompanies.forEach((c) => {
      if (!c || c.id == null) return;
      const existing = byId.get(String(c.id));
      if (existing) {
        byId.set(String(c.id), {
          ...existing,
          stripeFeatureEnabled:
            existing.stripeFeatureEnabled != null ? existing.stripeFeatureEnabled : c.stripeFeatureEnabled,
          myRole: existing.myRole || c.role,
          myPosition: existing.myPosition || c.position,
          deptLocationId: existing.deptLocationId || c.deptLocationId || null,
        });
        return;
      }
      byId.set(String(c.id), {
        id: c.id,
        name: c.name,
        logo: c.logo,
        corporateForm: c.corporateForm,
        branch: c.branch,
        email: c.email,
        website: c.website,
        isActive: c.isActive !== false,
        isKjuup: !!c.isKjuup,
        stripeFeatureEnabled: !!c.stripeFeatureEnabled,
        address: {
          houseNo: c.houseNo || "",
          street: c.street || "",
          postCode: c.postCode || "",
          city: c.city || "",
          country: c.country || "",
        },
        myRole: c.role,
        myPosition: c.position,
        deptLocationId: c.deptLocationId || null,
      });
    });
    return [...byId.values()];
  }, [overviewCompanies, memberCompanies]);
  const [pickedCompanyId, setPickedCompanyId] = useState(null);
  useEffect(() => {
    if (!active) return;
    const companyId = new URLSearchParams(location.search || "").get("company");
    if (companyId) setPickedCompanyId(companyId);
  }, [active, location.search]);
  const selectedCompany =
    companies.find((c) => String(c.id) === String(pickedCompanyId)) || companies[0] || null;
  const showCompanyEntry = (isPlus || (isIo && isKjuupPlusMember)) && !!(selectedCompany || isKjuupPlusMember);
  // Members without a company still get the entry so they can reach the "add company" panel.
  const companyEntry = showCompanyEntry
    ? selectedCompany || { id: null, name: strings.cockpit_tab_company || "Firma" }
    : null;
  useEffect(() => {
    if (!showCompanyEntry && tab === TAB_COMPANY) setTab(TAB_OVERVIEW);
  }, [showCompanyEntry, tab]);

  const loadRoomReports = useCallback(async (buildingId) => {
    const token = ++roomFetchRef.current;
    setRoomReportsLoading(true);
    try {
      const res = await callAPI(
        `/email/messages?folder=BRIEFING&buildingId=${encodeURIComponent(buildingId)}&page=1&limit=30`,
        {},
        "GET"
      );
      if (token !== roomFetchRef.current) return;
      setRoomReports(reportMessages(res?.data));
    } catch (_) {
      if (token === roomFetchRef.current) setRoomReports([]);
    } finally {
      if (token === roomFetchRef.current) setRoomReportsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!roomFilter) {
      roomFetchRef.current += 1;
      setRoomReports([]);
      setRoomReportsLoading(false);
      return undefined;
    }
    loadRoomReports(roomFilter);
    return undefined;
  }, [roomFilter, loadRoomReports]);

  const startReport = useCallback((buildingId) => startReportJob(buildingId, strings), [strings]);
  // A run started before a reload is followed again.
  useEffect(() => resumeReportJob(strings), [strings]);
  // The run's progress moves on while this cockpit shows it.
  useEffect(() => {
    if (!creatingReport) return undefined;
    const timer = window.setInterval(tickReportJob, 400);
    return () => window.clearInterval(timer);
  }, [creatingReport]);
  // A report created while the cockpit is open shows up at once.
  useEffect(() => onReportJobFinished(({ buildingId }) => {
    if (buildingId) loadRoomReports(buildingId);
    else refresh();
  }), [loadRoomReports, refresh]);

  const counts = overview?.counts;
  const rooms = overview?.rooms || [];
  // The overview only lists the latest rooms. A priority still has to find the
  // room its email or chat is filed in, including ones outside that short list.
  const priorityRooms = useMemo(() => {
    const list = [];
    const push = (room) => {
      if (!room || room.id == null || !room.name) return;
      if (list.some((item) => String(item.id) === String(room.id))) return;
      list.push({ id: room.id, name: room.name });
    };
    (rooms || []).forEach(push);
    (reduxProjects || []).forEach(push);
    (internalProjectsAll || []).forEach((company) => {
      (company.userProjectList || company.projects || []).forEach(push);
    });
    return list;
  }, [rooms, reduxProjects, internalProjectsAll]);
  const todosToday = overview?.todosToday || [];
  const latestReport = overview?.latestDailyBriefing || null;
  // The picker offers the rooms the day's report covers — the same set as the
  // "Aktive Projekträume" figure. Creating a single-room report makes that
  // room the latest one, so the covered set is kept until a new all-projects
  // report arrives. Without any report, rooms with activity in the last 24
  // hours are offered instead. The report's projects are looked up among all
  // the user's projects, not only the few latest rooms of the overview.
  const [coveredRooms, setCoveredRooms] = useState([]);
  useEffect(() => {
    const allScope = latestReport && !latestReport.buildingId ? latestReport : null;
    const covered = roomsCoveredByReport(allScope, priorityRooms);
    if (!covered.length) return;
    const ids = (list) => list.map((room) => String(room.id)).join(",");
    setCoveredRooms((prev) => (ids(prev) === ids(covered) ? prev : covered));
  }, [latestReport, priorityRooms]);
  const reportRooms = useMemo(() => {
    if (coveredRooms.length) return coveredRooms;
    const fromApi = overview?.reportActiveRooms;
    if (Array.isArray(fromApi) && fromApi.length) return fromApi;
    const active = roomsWithReportActivity(rooms, overview?.email?.recentIncoming);
    return active.length ? active : rooms;
  }, [overview, rooms, coveredRooms]);
  // The picked project stays selected. A quiet refresh (window focus, badge
  // change) rebuilds the list from the last 24 hours and can leave it out;
  // clearing the filter then threw the user back to all projects.
  const [pickedRoom, setPickedRoom] = useState(null);
  // TEMP debug for the project picker snapping back.
  useEffect(() => {
    console.log("[cockpit-debug] cockpit mounted");
    return () => console.log("[cockpit-debug] cockpit unmounted");
  }, []);
  useEffect(() => {
    console.log("[cockpit-debug] roomFilter =", roomFilter);
  }, [roomFilter]);
  useEffect(() => {
    if (!roomFilter) return;
    console.log(
      "[cockpit-debug] room reports",
      roomReportsLoading ? "(loading)" : "",
      roomReports.map((r) => ({ id: r.id, subject: r.subject, providerMessageId: r.providerMessageId, receivedAt: r.receivedAt }))
    );
  }, [roomFilter, roomReports, roomReportsLoading]);
  const pickReportRoom = useCallback(
    (id) => {
      console.log("[cockpit-debug] pick", id);
      setRoomFilter(id);
      setPickedRoom(id ? reportRooms.find((room) => String(room.id) === String(id)) || null : null);
    },
    [reportRooms]
  );
  const pickerRooms = useMemo(() => {
    if (!roomFilter || !pickedRoom || reportRooms.some((room) => String(room.id) === String(roomFilter))) return reportRooms;
    return [pickedRoom, ...reportRooms];
  }, [reportRooms, roomFilter, pickedRoom]);
  const visibleLatest = useMemo(() => {
    if (!latestReport) return null;
    if (!roomFilter) return latestReport.buildingId ? null : latestReport;
    return String(latestReport.buildingId) === String(roomFilter) ? latestReport : null;
  }, [latestReport, roomFilter]);
  const visibleReports = roomFilter ? roomReports : reports;

  const hideChats = isIo && chatsEnabled === false;

  const reportPriorities = useMemo(() => prioritiesFromEmail(visibleLatest), [visibleLatest]);

  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarkedCount, setBookmarkedCount] = useState(null);
  // The "Heute zu erledigen" card's nodes the report portals its task list
  // and its "Mehrere löschen" into.
  const [tasksHost, setTasksHost] = useState(null);
  const [tasksActionHost, setTasksActionHost] = useState(null);
  const [reportTasks, setReportTasks] = useState([]);
  const [dismissedPriorityTexts, setDismissedPriorityTexts] = useState([]);
  // The sync inputs are read through a ref so the arrays coming off `overview`
  // cannot restart the effect — only a reload or another report may do that.
  const syncInputRef = useRef(null);
  syncInputRef.current = { report: visibleLatest, priorities: reportPriorities, rooms: priorityRooms, todosToday };
  const latestReportId = visibleLatest?.id || null;
  // The mount run's task-list fetch ({ at, promise }). The effect below runs on
  // mount and again the moment the overview lands; the second run reuses that
  // fetch instead of asking for both 200-row lists again.
  const taskFetchRef = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    // The list holds the bookmarks and the (dated) tasks the report created.
    const apply = ({ items, total }, reportItems = []) => {
      if (cancelled) return;
      const ids = new Set(items.map((item) => String(item.todoId)));
      setBookmarks(items.concat(reportItems.filter((item) => !ids.has(String(item.todoId)))));
      setBookmarkedCount(total);
    };
    const fetchLists = () => {
      // Only the mount fetch (before the overview) is handed on, once.
      const recent = taskFetchRef.current;
      taskFetchRef.current = null;
      if (recent && lastLoadedAt && Date.now() - recent.at < TASK_FETCH_REUSE_MS) return recent.promise;
      const promise = Promise.all([fetchBookmarkedTodos(), fetchReportTasks([]).catch(() => [])]);
      if (!lastLoadedAt) taskFetchRef.current = { at: Date.now(), promise };
      promise.catch(() => {
        if (taskFetchRef.current && taskFetchRef.current.promise === promise) taskFetchRef.current = null;
      });
      return promise;
    };
    (async () => {
      try {
        const [loaded, sorted] = await fetchLists();
        apply(loaded, sorted);
        const { report, priorities, rooms: syncRooms, todosToday: dated } = syncInputRef.current;
        // Matched against every task the cockpit already shows — bookmarks and
        // dated report tasks — so a reworded row links to its task instead of
        // making a second one.
        const { created, repaired, todos, dismissed } = await syncPriorityBookmarks({
          report,
          priorities,
          rooms: syncRooms,
          bookmarks: loaded.items.concat(sorted),
          dated,
        });
        if (cancelled) return;
        setReportTasks(todos);
        setDismissedPriorityTexts(dismissed || []);
        if (!created && !repaired) return;
        const [reloaded, reportItems] = await Promise.all([
          fetchBookmarkedTodos(),
          fetchReportTasks((todos || []).map((task) => task.id)).catch(() => []),
        ]);
        apply(reloaded, reportItems);
      } catch (_) {
        if (!cancelled) setBookmarkedCount((prev) => (prev == null ? 0 : prev));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, lastLoadedAt, latestReportId]);

  const notedPriorities = useMemo(() => new Set(reportTasks.map((task) => task.text)), [reportTasks]);
  // Today's tasks the report created are listed under the report's headers,
  // not a second time under "Aufgaben heute" — any task sorted under a header is.
  const todayWithoutReportTasks = useMemo(() => {
    const ids = new Set(reportTasks.map((task) => String(task.id)));
    return todosToday.filter((item) => !ids.has(String(item.todoId)) && !item.bookmarkCategory);
  }, [todosToday, reportTasks]);
  // Beside the report, today's own tasks are listed under "Fristen & Aufgaben"
  // with the rest, in the same rows; their room stands in for the project.
  const listTasks = useMemo(() => {
    const ids = new Set(bookmarks.map((item) => String(item.todoId)));
    const own = todayWithoutReportTasks
      .filter((item) => !ids.has(String(item.todoId)))
      .map((item) => ({ ...item, bookmarkProject: item.roomName || item.groupName || "" }));
    return own.length ? bookmarks.concat(own) : bookmarks;
  }, [bookmarks, todayWithoutReportTasks]);
  // The day strip over Meine Aufgaben (a week; null is today, so midnight moves it on).
  const todayKey = cockpitDayKey(new Date());
  const shownDay = taskDay || todayKey;
  // A dot on the strip's days with something on them ("appointment" wins over
  // "tasks"): each task on its own day; overdue and undated ones on today too,
  // where today's list shows them.
  const dayMarks = useMemo(() => {
    const marks = {};
    const mark = (at, group) => {
      if (group === "appointment") marks[at] = "appointment";
      else if (!marks[at]) marks[at] = "tasks";
    };
    listTasks.forEach((item) => {
      if (!item || ["completed", "confirmed"].includes(String(item.status || ""))) return;
      const day = cockpitTaskDay(item);
      const group = cockpitGroupOf(item.bookmarkCategory);
      if (day) mark(day, group);
      if (!day || day < todayKey) mark(todayKey, group);
    });
    return marks;
  }, [listTasks, todayKey]);
  // Deleting a priority has to take the report's own row with it, not only the
  // task it produced — otherwise the row comes back on the next render.
  // Takes one text or a whole selection — several go up in a single write.
  const onPriorityDismissed = useCallback(
    async (texts) => {
      if (!latestReportId) return;
      setDismissedPriorityTexts(await dismissPriority(latestReportId, texts));
    },
    [latestReportId]
  );

  // Navigation callbacks
  // The KPI tiles switch the list's groups on and off (the last one stays on),
  // and scroll to it only while it is off screen (single-column layout).
  const toggleTaskGroup = (group) => {
    setTaskGroups((prev) => {
      if (prev.includes(group)) return prev.length > 1 ? prev.filter((key) => key !== group) : prev;
      return COCKPIT_TASK_GROUPS.map((g) => g.key).filter((key) => key === group || prev.includes(key));
    });
    const target = document.getElementById(MY_TASKS_CARD_ID) || document.getElementById(BOOKMARKED_PRIORITIES_ID);
    if (!target) return;
    const top = target.getBoundingClientRect().top;
    if (top >= 0 && top < window.innerHeight - 120) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    target.classList.add("cockpit-flash");
    window.setTimeout(() => target.classList.remove("cockpit-flash"), 1600);
  };
  const goMessages = (view) => openMessagesPage(dispatch, history, { view });
  const goRoomsList = () => openRoomsList(dispatch, history);
  const goProfile = () => openSettings(dispatch, history);
  const goSettings = (section) => openSettings(dispatch, history, section);
  // Firmeneinstellungen (with the Admin-Panel sections for company admins). The
  // flags only /user/cockpit knows travel along (media export server row, index counts).
  const goCompanySettings = async (company) => {
    if (!company?.id) return;
    await openCompanySettingsFor(dispatch, history, company, {
      state: {
        mediaExportEnabled: company.mediaExportEnabled === true,
        employeeCount: company.employeeCount,
        documentCount: company.documentCount,
      },
    });
  };
  const goRequestIo = () => openExternal(appConfig.KJUUP_IO_ANFRAGE_URL);
  const goEnquirePlus = () => openExternal(appConfig.KJUUP_PLUS_ANFRAGE_URL);
  const goConnectMailbox = () => goSettings("email-accounts");
  // "Kanäle": the Kjuup-Chat switch is on the settings list itself ("chats" scrolls to it).
  const goChannel = (key) =>
    goSettings(
      key === "email" ? "email-accounts" : key === "forward" ? "email-forward" : key === "teams" ? "teams" : "chats"
    );

  // The assignment list is only for users who keep their own mail program.
  // With the Kjuup email client on, mail is handled in Nachrichten.
  const recentIncoming = overview?.email?.recentIncoming;
  const incomingEmails = overview?.email?.hasMailbox
    ? (Array.isArray(recentIncoming) ? recentIncoming : [])
    : null;

  // Fristen & Aufgaben / Termine: the rows under those two headers in Meine
  // Aufgaben, so the tiles and the list agree. Without the report's list,
  // today's tasks and the open bookmarks, the appointments counted apart.
  const openAppointments = bookmarks.filter(
    (item) => item && item.bookmarkCategory === "appointment" && !["completed", "confirmed"].includes(String(item.status || ""))
  ).length;
  const appointmentCount = listedCounts ? listedCounts.appointment : openAppointments;
  const taskCount = listedCounts
    ? listedCounts.tasks
    : Math.max(0, (counts?.today || 0) + (bookmarkedCount || 0) - openAppointments);

  const renderOverview = () => (
    <>
      {/* The tiles only without the daily report: with it, Meine Aufgaben
          keeps the same two switches as chips. */}
      {showReports ? null : (
        <CockpitKpis
          strings={strings}
          tasks={taskCount}
          appointments={appointmentCount}
          bookmarksLoading={bookmarkedCount == null}
          loading={loading && !overview}
          groups={taskGroups}
          onToggleGroup={toggleTaskGroup}
        />
      )}

      <CockpitChannels
        strings={strings}
        chat={{ on: !hideChats }}
        email={{ visible: showEmailFeatures, accounts: overview?.email?.accounts, loading: loading && !overview }}
        teamsAllowed={showEmailFeatures}
        refreshKey={lastLoadedAt}
        onOpen={goChannel}
      />

      {isIo && !kjuupIoSetupDone ? <CockpitSetupCard strings={strings} onStart={() => setSetupOpen(true)} /> : null}

      {/* Kjuup / Kjuup+ (no daily report): the bookmarked tasks get their own card, as on mobile.
          With the report they sit inside the report card below. */}
      {!showReports && bookmarks.length ? (
        <Card className="cockpit-bookmarks-card">
          <section className="cockpit-briefing-section" id={BOOKMARKED_PRIORITIES_ID}>
            <h4>{strings.cockpit_bookmarked_priorities || "Vorgemerkte Aufgaben"}</h4>
            <CockpitBookmarkedPriorities
              strings={strings}
              rooms={rooms}
              items={bookmarks.filter((item) => taskGroups.includes(cockpitGroupOf(item && item.bookmarkCategory)))}
              onChanged={refresh}
            />
          </section>
        </Card>
      ) : null}

      {!isIo && !subscriptionPending ? (
        <CockpitKjuupUpsell
          strings={strings}
          language={language}
          personaInfo={personaInfo}
          showPlus={!isPlus}
          onRequestIo={goRequestIo}
          onEnquirePlus={goEnquirePlus}
          onIoActivated={() => {
            if (!kjuupIoSetupDone) setSetupOpen(true);
          }}
        />
      ) : null}

      {/* "Heute wichtig" (the daily report's Top 5) across the top, "Meine Aufgaben"
          below it: Fristen & Aufgaben left, Termine right. */}
      {showReports ? (
        <section className="cockpit-day cockpit-day--split">
          <div className="cockpit-day-main">
          <CockpitDailyReportCard
            key={roomFilter || "all"}
            strings={strings}
            language={language}
            reports={visibleReports}
            latest={visibleLatest}
            email={overview?.email}
            hasMailbox={!!overview?.email?.hasMailbox}
            loading={roomFilter ? roomReportsLoading : loading && !overview}
            onMarkRead={markReportRead}
            onDelete={deleteReport}
            onConnectMailbox={goConnectMailbox}
            rooms={rooms}
            reportRooms={pickerRooms}
            notedPriorities={notedPriorities}
            dismissedPriorities={dismissedPriorityTexts}
            onPriorityDismissed={onPriorityDismissed}
            bookmarks={listTasks}
            onTodoCreated={refresh}
            onTaskChanged={refresh}
            roomFilter={roomFilter}
            onRoomFilterChange={pickReportRoom}
            onRefreshReport={() => startReport(roomFilter)}
            refreshingReport={creatingReport}
            roomSelected={!!roomFilter}
            creatingReport={creatingReport}
            reportProgress={reportProgress}
            onCreateReport={roomFilter ? () => startReport(roomFilter) : null}
            tasksTarget={tasksHost}
            tasksActionTarget={tasksActionHost}
            onGroupCounts={onGroupCounts}
            taskGroups={taskGroups}
            taskDay={shownDay === todayKey ? null : shownDay}
          />
          </div>
          <aside className="cockpit-day-side" ref={tasksSideRef}>
            {openTask ? <CockpitTaskView key={openTask.todoId} task={openTask} strings={strings} onClose={closeTask} /> : null}
            {/* Kept mounted under an open task: the report draws its task list into this card. */}
            <div className="cockpit-day-side-list" hidden={!!openTask}>
            <CockpitMyTasksCard
              strings={strings}
              language={language}
              dateLabel={formatGreetingDate(new Date(`${shownDay}T12:00:00`), language)}
              day={shownDay}
              today={todayKey}
              dayMarks={dayMarks}
              onPickDay={(day) => setTaskDay(day === todayKey ? null : day)}
              // Once the report's list is drawn into the card, today's tasks are in it.
              // They are all "Fristen & Aufgaben" (no appointment category).
              items={listedCounts || !taskGroups.includes("tasks") || shownDay !== todayKey ? [] : todayWithoutReportTasks}
              groups={taskGroups}
              groupCounts={{ tasks: taskCount, appointment: appointmentCount }}
              onToggleGroup={toggleTaskGroup}
              loading={loading && !overview}
              currentUserId={user?.id}
              onOpenItem={openTaskHere}
              hostRef={setTasksHost}
              actionRef={setTasksActionHost}
            />
            </div>
          </aside>
        </section>
      ) : null}

      {isIo && showEmailFeatures && emailMode === "reports" ? (
        <div ref={emailSectionRef}>
          <CockpitEmailBanner
            strings={strings}
            language={language}
            email={overview?.email}
            onConnect={goConnectMailbox}
            onOpenInbox={() => goMessages("email")}
            onOpenSettings={() => goSettings("email-accounts")}
            onOpenRooms={goRoomsList}
          >
            {incomingEmails ? (
              <CockpitIncomingEmails strings={strings} language={language} items={incomingEmails} onChanged={refresh} />
            ) : null}
          </CockpitEmailBanner>
        </div>
      ) : null}

    </>
  );

  // Sidebar › Kjuup+ lands on the booking card (focus changes on every click).
  const searchParams = new URLSearchParams(location.search || "");
  const aboFocus = searchParams.get("section") === "kjuup-plus-abo" ? searchParams.get("focus") || "1" : null;

  const renderCompany = () => (
    <CockpitCompanyPanel
      strings={strings}
      company={selectedCompany}
      companies={companies}
      onSelectCompany={setPickedCompanyId}
      internalRooms={rooms.filter((r) => {
        if (!r.isInternal) return false;
        if (!selectedCompany || r.companyId == null) return true;
        return String(r.companyId) === String(selectedCompany.id);
      })}
      onOpenCompanySettings={goCompanySettings}
      onOpenDocuments={(id) => openCompanyDocuments(dispatch, history, id)}
      onOpenCompanies={() => openMyCompanies(dispatch, history)}
      onAddCompany={() => openAddCompany(dispatch, history)}
      extra={
        <>
          {selectedCompany && selectedCompany.isKjuup !== false ? (
            <CockpitKjuupPlusAbo
              key={`abo-${selectedCompany.id}`}
              strings={strings}
              language={language}
              companyId={selectedCompany.id}
              focus={aboFocus}
            />
          ) : null}
          {selectedCompany ? (
            <CompanyUnpublishedArticles
              key={`articles-${selectedCompany.id}`}
              className="cockpit-unpublished"
              company={{
                id: selectedCompany.id,
                role: selectedCompany.myRole || selectedCompany.role,
                position: selectedCompany.myPosition || selectedCompany.position,
                deptLocationId: selectedCompany.deptLocationId,
                isKjuup: selectedCompany.isKjuup,
              }}
            />
          ) : null}
          {viewerManagesCompanyRooms(selectedCompany) ? (
            <CockpitRoomAdminCard key={selectedCompany.id} strings={strings} companyId={selectedCompany.id} />
          ) : null}
        </>
      }
    />
  );

  return (
    <CockpitTaskContext.Provider value={openTaskHere}>
    <div className={`cockpit${isDeskTopResolution ? "" : " cockpit--compact"}`} data-persona={persona}>
      <div className="cockpit-content">
        <CockpitHeader
          strings={strings}
          language={language}
          user={user}
          packageLabel={personaInfo.packageLabel}
          counts={counts}
          company={companyEntry}
          companyCount={companies.length}
          companyActive={tab === TAB_COMPANY}
          onOpenCompanyPanel={() => {
            setOpenTask(null);
            setTab(tab === TAB_COMPANY ? TAB_OVERVIEW : TAB_COMPANY);
          }}
          onOpenProfile={goProfile}
          onOpenSettings={() => goSettings()}
          onBack={
            tab === TAB_COMPANY
              ? () => {
                  setTab(TAB_OVERVIEW);
                  const params = new URLSearchParams(location.search || "");
                  params.delete("tab");
                  params.delete("focus");
                  params.delete("section");
                  const search = params.toString();
                  history.replace({ pathname: location.pathname, search: search ? `?${search}` : "" });
                }
              : null
          }
        />
        {error && !overview ? (
          <div className="cockpit-card cockpit-error">
            <span>{strings.cockpit_load_error || "Das Cockpit konnte nicht geladen werden."}</span>
            <button type="button" className="kj-btn kj-btn--secondary" onClick={refresh}>
              {strings.cockpit_retry || "Erneut versuchen"}
            </button>
          </div>
        ) : null}
        {tab === TAB_COMPANY && showCompanyEntry ? (
          renderCompany()
        ) : (
          renderOverview()
        )}
      </div>
      <KjuupIoSetupModal open={setupOpen} onClose={() => setSetupOpen(false)} onSaved={() => refresh()} />
    </div>
    </CockpitTaskContext.Provider>
  );
}
